/**
 * Order custom tab component
 *
 * @author Ryan Sweeney <rsweeney@buildcentrix.com>
 * @copyright (c) 2024 Build Centrix
 */
import { Component, Inject, OnInit, ViewEncapsulation } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { WdOrdersV2Service } from '../../../services/orders/wd-orders-v2.service';
import {
  IOrderTabs,
  ITableColumnOptions,
} from '../../../interfaces/orders/order-interfaces';
import angular from 'angular';
import { WdUserService } from '../../../services/users/wd-users.service';
import { WdLocalEnvironmentService } from '../../../services/environment/wd-local-environment.service';
import { ActivatedRoute, Params, Router } from '@angular/router';

@Component({
  selector: 'app-order-custom-tab-dialog',
  templateUrl: './order-custom-tab.component.html',
  styleUrls: ['./order-custom-tab.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class OrderCustomTabComponent implements OnInit {
  // Booleans
  canDelete = false;
  isEditMode = true;

  // Selections
  orderType: string;
  selectedTab: IOrderTabs;

  // Max custom columns
  customTabIcon = this.wdOrdersService.defaultCustomTabIcon;
  customTabLabel = 'Custom Tab';
  customTabLabelMaxLength = 20;
  customTabColumnsMin = 1;
  customTabColumnsMax = 10;
  customTabColumnOptions: Array<ITableColumnOptions> = [];
  customTabColumnsSelected = 0;

  // Callbacks
  onSuccess: Function;

  /**
   * Constructor
   * @since 4.77.0
   * @memberof OrderCustomTabComponent
   */
  constructor(
    public dialogRef: MatDialogRef<OrderCustomTabComponent>,
    private route: ActivatedRoute,
    private router: Router,
    private wdEnvironmentService: WdLocalEnvironmentService,
    public wdOrdersService: WdOrdersV2Service,
    public wdUserService: WdUserService,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) {
    this.orderType = data.orderType;
    this.selectedTab = data.selectedTab;
    this.isEditMode = !!this.selectedTab;
    this.onSuccess = data.onSuccessCallback;
  }

  /**
   * On Modal Save
   * @since 4.77.0
   * @memberof OrderCustomTabComponent
   */
  async onDelete(): Promise<void> {
    const customTabKey = this.selectedTab.key;
    // Add/update tab and then reinitialize tabs
    await this.wdUserService.removeOrderListCustomTab(
      this.wdEnvironmentService.user.index,
      customTabKey,
    );
    await this.wdOrdersService.initializeUserCustomTabs();
    // Get first available visible tab
    const firstAvailableTab = this.wdOrdersService.tabbedOrderContent.filter(
      (tab: IOrderTabs) => tab.isVisible,
    )[0];
    // Select the first available visible tab and close the modal
    this.reloadTabsAndCloseModal(firstAvailableTab);
  }

  /**
   * On Save
   * @since 4.77.0
   * @memberof OrderCustomTabComponent
   */
  async onSave(): Promise<void> {
    const customTab: IOrderTabs = {
      label: this.customTabLabel,
      iconKey: this.customTabIcon,
      columns: this.customTabColumnOptions
        .filter((column: any) => column.selected)
        .map((column: any) => ({ id: column.key })),
    };
    if (this.isEditMode) {
      customTab.key = this.selectedTab.key;
    }
    // Add/update tab and then reinitialize tabs
    await this.wdUserService.manageOrderListCustomTab(
      this.wdEnvironmentService.user.index,
      customTab,
    );
    await this.wdOrdersService.initializeUserCustomTabs();
    // Get added/updated tab
    const selectedCustomTab = this.isEditMode
      ? this.wdOrdersService.tabbedOrderContent.find(
          (tab: IOrderTabs) => tab.key === customTab.key,
        )
      : this.wdOrdersService.tabbedOrderContent[
          this.wdOrdersService.tabbedOrderContent.length - 1
        ];
    // Get added/updated tab
    this.wdOrdersService.orderViewSettings.selectedTab = selectedCustomTab;
    // Select the new custom tab and close the modal
    this.reloadTabsAndCloseModal(selectedCustomTab);
  }

  /**
   * Reload and Select Tabs and Close Modal
   * @since 4.77.0
   * @memberof OrderCustomTabComponent
   */
  reloadTabsAndCloseModal(customTab: IOrderTabs) {
    const tabKey = customTab.key;
    const queryParams: Params = { tab: tabKey };
    // Update the route query param
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      queryParamsHandling: 'merge', // remove to replace all query params by provided
    });
    // Reinitialize tabs
    this.wdOrdersService.initColumnToggleOptions(this.orderType, tabKey);
    this.dialogRef.close(false);
    this.onSuccess();
  }

  /**
   * On Init
   * @since 4.77.0
   * @memberof OrderCustomTabComponent
   */
  ngOnInit() {
    let columnOptions: Array<ITableColumnOptions> = angular.copy(
      this.wdOrdersService.allColumnOptions,
    );
    const visibleTabs = angular.copy(
      this.wdOrdersService.tabbedOrderContent.filter(
        (tab: IOrderTabs) => tab.isVisible,
      ),
    );
    // At least one other tab must be visible to delete
    this.canDelete = visibleTabs.length > 1;
    if (this.isEditMode) {
      // Set saved label
      this.customTabLabel = angular.copy(this.selectedTab.label);
      // Set column options
      columnOptions = angular.copy(
        this.wdOrdersService.orderListColumnsOptions,
      );
    }
    // Set column options
    this.customTabColumnOptions = columnOptions;
    // Set custom columns count
    this.setCustomColumnsCount();
  }

  /**
   * On Modal Dismiss
   * @since 4.77.0
   * @memberof OrderCustomTabComponent
   */
  onDismiss(): void {
    // Close the dialog, return false
    this.dialogRef.close(false);
  }

  /**
   * Set custom columns count (for validation)
   * @since 4.77.0
   * @memberof OrderCustomTabComponent
   */
  setCustomColumnsCount(): void {
    this.customTabColumnsSelected = this.customTabColumnOptions.filter(
      (option: ITableColumnOptions) => {
        return option.canToggle && option.selected;
      },
    ).length;
  }
}
